import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listLaboratories,
  listLabStock,
  downloadLabTemplate,
  bulkUploadLab,
  updateLabStockRow,
  deleteLabStockRow,
  setLabTestImage,
} from '../../api/laboratoryStock.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { useDebounce } from '../../hooks/useDebounce.js'
import { useAuth } from '../../auth/AuthProvider.jsx'
import AddLabTestModal from './AddLabTestModal.jsx'

/**
 * Laboratory catalogue self-service page. Lab staff manage the tests
 * their laboratory offers — adding, pricing, setting turnaround time,
 * toggling availability. Lab staff are auto-scoped to their own lab
 * via the LaboratoryStaffLink; platform admins pick the target lab
 * from a dropdown.
 */
export default function LaboratoryCatalogue() {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const linkedLabId = user?.laboratory_profile?.id ?? null
  const linkedLabName = user?.laboratory_profile?.name ?? ''

  const labsQ = useQuery({
    queryKey: ['laboratories-list'],
    queryFn: listLaboratories,
    enabled: isAdmin,
  })
  const [labId, setLabId] = useState(linkedLabId)
  // Default an admin's selection to the first lab once the list loads.
  // Was previously a render-time setState — moved to an effect so React
  // strict mode stops warning and the value can't flicker.
  useEffect(() => {
    if (isAdmin && !labId && labsQ.data?.[0]?.id) {
      setLabId(labsQ.data[0].id)
    }
  }, [isAdmin, labId, labsQ.data])

  if (!isAdmin && !linkedLabId) {
    return (
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Catalogue self-service for laboratory staff"
        />
        <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
          <b>Account not linked to a laboratory yet.</b>
          <p className="mt-1">
            Ask the admin to bind your user
            (<span className="font-mono">{user?.username}</span>) via Django
            admin → Laboratory → Laboratory staff links → Add.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Manage the tests your laboratory offers: download the template, fill in price + turnaround, upload it back. Edit individual rows below."
          right={
            <div className="flex items-center gap-3">
              <a
                href="/lab-catalogue-guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-wantok-700 hover:text-wantok-800 underline whitespace-nowrap"
              >
                Laboratory user guide (PDF)
              </a>
              <a
                href="/lab-sop.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-wantok-700 hover:text-wantok-800 underline whitespace-nowrap"
              >
                Laboratory staff SOP (PDF)
              </a>
            </div>
          }
        />
        {isAdmin ? (
          <div className="mt-2">
            <label className="text-xs uppercase tracking-wide text-slate-500">
              Laboratory
              <span className="ml-2 text-amber-700 normal-case font-normal">(admin — pick any)</span>
            </label>
            <select
              value={labId || ''}
              onChange={(e) => setLabId(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2 text-sm bg-white"
            >
              {(labsQ.data || []).map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-2 text-sm">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">Laboratory</span>
            <span className="font-semibold text-slate-800">{linkedLabName}</span>
            {user?.laboratory_profile?.is_lab_admin && (
              <span className="ml-2 text-[10px] uppercase border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-100">
                lab admin
              </span>
            )}
          </div>
        )}
      </Card>

      {labId && (
        <>
          <TemplateCard />
          <UploadCard labId={labId} />
          <StockListCard labId={labId} />
        </>
      )}
    </div>
  )
}

function TemplateCard() {
  const CATEGORIES = ['', 'Haematology', 'Biochemistry', 'Microbiology',
                      'Serology', 'Endocrine', 'Coagulation', 'Tumour markers',
                      'Immunology', 'Urinalysis', 'Stool', 'Point of care']
  const [category, setCategory] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)

  async function handleDownload() {
    setError(null); setDownloading(true)
    try {
      const blob = await downloadLabTemplate({ category })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `wantok_lab_catalogue_template${category ? '_' + category.toLowerCase() : ''}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) { setError(e) }
    finally { setDownloading(false) }
  }

  return (
    <Card>
      <CardHeader title="1. Download template"
        subtitle="A CSV with every lab test in the global catalogue. Fill in price, turnaround_time, is_offered on the rows you actually run; leave the rest blank." />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-500">Category:</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c || 'all'} value={c}>{c || 'All categories'}</option>
            ))}
          </select>
        </label>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {error && <div className="mt-3"><ErrorBanner error={error} /></div>}
    </Card>
  )
}

function UploadCard({ labId }) {
  const qc = useQueryClient()
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)

  const mut = useMutation({
    mutationFn: (f) => bulkUploadLab({ file: f, laboratoryId: labId }),
    onSuccess: (data) => {
      setResult(data)
      qc.invalidateQueries({ queryKey: ['lab-stock', labId] })
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    },
  })

  return (
    <Card>
      <CardHeader title="2. Upload your filled-in CSV"
        subtitle="Multipart upload — only rows where you filled in price, turnaround_time, notes, or set is_offered=true are processed. Re-uploading is safe." />
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef} type="file" accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        <button
          onClick={() => file && mut.mutate(file)}
          disabled={!file || mut.isPending}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
        >
          {mut.isPending ? 'Uploading…' : 'Upload'}
        </button>
        {file && <span className="text-xs text-slate-500">{file.name} · {Math.round(file.size / 1024)} KB</span>}
      </div>
      {mut.error && <div className="mt-3"><ErrorBanner error={mut.error} /></div>}
      {result && <UploadResult data={result} />}
    </Card>
  )
}

function UploadResult({ data }) {
  const s = data?.summary || {}
  const errors = data?.errors || []
  const proposals = data?.proposals || []
  const Stat = ({ label, value, colour = 'slate' }) => {
    const map = {
      slate: 'border-slate-200 bg-slate-50 text-slate-700',
      emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
      sky: 'border-sky-200 bg-sky-50 text-sky-800',
      amber: 'border-amber-200 bg-amber-50 text-amber-800',
      rose: 'border-rose-200 bg-rose-50 text-rose-800',
    }
    return (
      <div className={`border rounded px-2 py-1.5 text-center ${map[colour]}`}>
        <div className="text-lg font-semibold">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      </div>
    )
  }
  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Rows processed" value={s.rows_processed} />
        <Stat label="Created" value={s.stock_created} colour="emerald" />
        <Stat label="Updated" value={s.stock_updated} colour="sky" />
        <Stat label="Proposals" value={s.proposals_created} colour="amber" />
        <Stat label="Errors" value={s.errors} colour={s.errors ? 'rose' : 'slate'} />
      </div>
      {errors.length > 0 && (
        <div className="border border-rose-200 bg-rose-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-1">Row errors ({errors.length})</div>
          <ul className="text-xs text-rose-800 space-y-0.5 max-h-40 overflow-auto">
            {errors.map((e, i) => <li key={i}><span className="font-mono">row {e.row}</span> · {e.error}</li>)}
          </ul>
        </div>
      )}
      {proposals.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">
            Filed as test proposals ({proposals.length}) — admin will review
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 max-h-40 overflow-auto">
            {proposals.map((p) => (
              <li key={p.proposal_id}>
                <span className="font-mono">row {p.row}</span> · <b>{p.test_name}</b>
                {p.sample_type && <> ({p.sample_type})</>}
                <span className="text-amber-600"> #{p.proposal_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function StockListCard({ labId }) {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const debounced = useDebounce(search, 250)
  const stockQ = useQuery({
    queryKey: ['lab-stock', labId, debounced],
    queryFn: () => listLabStock({ laboratoryId: labId, search: debounced }),
    enabled: Boolean(labId),
  })
  const rows = useMemo(() => stockQ.data || [], [stockQ.data])
  const filtering = Boolean(debounced?.trim())
  const subtitle = filtering
    ? `Showing ${rows.length} match${rows.length === 1 ? '' : 'es'} for "${debounced}". Clear the filter to see your full catalogue.`
    : `${rows.length} test${rows.length === 1 ? '' : 's'} in your catalogue. Add tests one at a time via + Add lab test, or use CSV upload for bulk. Edit inline. Delete = you no longer offer it.`

  // Refetch after modal-driven create — belt-and-braces alongside the
  // modal's own queryClient invalidation.
  function afterCreated() {
    stockQ.refetch()
  }

  return (
    <Card>
      <CardHeader title="3. Current catalogue"
        subtitle={subtitle}
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="text-xs px-2.5 py-1 rounded bg-wantok-700 hover:bg-wantok-800 text-white font-medium"
              title="Add one test to your catalogue (search the global formulary)"
            >
              + Add lab test
            </button>
            <button onClick={() => stockQ.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>
          </div>
        }
      />
      <AddLabTestModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={afterCreated}
      />
      <input
        value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by test name, short code, or category…"
        className="w-full border rounded px-3 py-2 text-sm mb-3"
      />
      {stockQ.error && <ErrorBanner error={stockQ.error} onRetry={() => stockQ.refetch()} />}
      {stockQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic">
          No tests yet. Upload a CSV above to populate.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="p-2 w-12"></th>
                <th className="text-left p-2">Test</th>
                <th className="text-left p-2">Sample</th>
                <th className="text-right p-2">Price (PGK)</th>
                <th className="text-left p-2">Turnaround</th>
                <th className="text-center p-2">Offered</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <StockRow
                  key={r.id} row={r}
                  isAdmin={isAdmin}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['lab-stock', labId] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function StockRow({ row, isAdmin, onChanged }) {
  const [price, setPrice] = useState(row.price ?? '')
  const [tat, setTat] = useState(row.turnaround_time || '')
  const [offered, setOffered] = useState(row.is_offered)

  // Re-sync local state whenever the server-side row changes — e.g. after
  // a CSV bulk upload invalidates the catalogue query and the row arrives
  // with newly-saved price / turnaround / offered. Without this, useState's
  // initialiser only runs at mount and the inputs render blank even though
  // the server has the values. Using `last_updated` as the trigger lets the
  // user keep typing in a row that nobody else has touched (we won't clobber
  // mid-edit work just because of an unrelated refetch).
  useEffect(() => {
    setPrice(row.price ?? '')
    setTat(row.turnaround_time || '')
    setOffered(row.is_offered)
  }, [row.id, row.last_updated])

  const dirty = (
    String(price) !== String(row.price ?? '') ||
    tat !== (row.turnaround_time || '') ||
    offered !== row.is_offered
  )

  const save = useMutation({
    mutationFn: () => updateLabStockRow(row.id, {
      price: price === '' ? null : price,
      turnaround_time: tat,
      is_offered: Boolean(offered),
    }),
    onSuccess: onChanged,
  })

  const del = useMutation({
    mutationFn: () => deleteLabStockRow(row.id),
    onSuccess: onChanged,
  })

  return (
    <tr className={dirty ? 'bg-amber-50/40' : ''}>
      <td className="p-2">
        <CatalogueImageThumb
          url={row.test_image_url}
          alt={row.test_name}
          editable={isAdmin}
          onUpload={async (file) => {
            await setLabTestImage(row.lab_test, file)
            onChanged()
          }}
          onClear={async () => {
            await setLabTestImage(row.lab_test, null)
            onChanged()
          }}
        />
      </td>
      <td className="p-2">
        <div className="font-medium">
          {row.test_name}
          {row.short_code && <span className="ml-2 text-[10px] uppercase text-slate-500 font-mono">{row.short_code}</span>}
        </div>
        <div className="text-[11px] text-slate-500">
          {row.category}
          {row.loinc_code && <span className="ml-2 font-mono text-slate-400">{row.loinc_code}</span>}
        </div>
      </td>
      <td className="p-2 text-xs text-slate-500">{row.sample_type || '—'}</td>
      <td className="p-2 text-right">
        <input type="number" step="0.01" value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-24 border rounded px-2 py-1 text-xs text-right"
        />
      </td>
      <td className="p-2">
        <input value={tat}
          onChange={(e) => setTat(e.target.value)}
          placeholder="e.g. 30 min"
          className="w-32 border rounded px-2 py-1 text-xs"
        />
      </td>
      <td className="p-2 text-center">
        <input type="checkbox" checked={Boolean(offered)}
          onChange={(e) => setOffered(e.target.checked)}
        />
      </td>
      <td className="p-2 text-right whitespace-nowrap">
        <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
          className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-2 py-1 disabled:opacity-40">
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => { if (window.confirm(`Stop offering ${row.test_name}?`)) del.mutate() }}
          disabled={del.isPending}
          className="ml-2 text-xs text-rose-600 hover:underline disabled:opacity-40">
          Delete
        </button>
      </td>
    </tr>
  )
}
